import { useQuery } from '@tanstack/react-query';
import type { AppSetting } from '@peles/shared';
import { fetchAppSettingByVarCode } from '../api/app-settings';

/**
 * Fetch a single app setting value by varCode.
 * Returns the typed value (string | number | boolean | Date | null).
 *
 * Reading a setting is PLANNER+; pass `enabled: false` where a client may be
 * the one looking, rather than asking for a 403.
 */
export function useAppSettingValue(
  varCode: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<string | number | boolean | Date | null>({
    queryKey: ['appSettingValue', varCode],
    queryFn: async () => {
      const setting: AppSetting = await fetchAppSettingByVarCode(varCode);
      if (!setting || !setting.isActive || setting.value === null) {
        return null;
      }
      switch (setting.varType) {
        case 'NUMBER':
          return Number(setting.value);
        case 'BOOLEAN':
          return setting.value === 'true';
        case 'DATE':
          return new Date(setting.value);
        default:
          return setting.value;
      }
    },
    enabled: enabled && !!varCode,
    staleTime: 5 * 60 * 1000,
  });
}
